// DeckPreviewPane -- right-pane live preview for the deck builder (Lane 8 rev3,
// 2026-06-27; single-active-slide default). Presentational: the parent
// (DeckAssembler) owns the export flow + stale-signature derivation + the
// preview-document lifecycle, and passes the ready-to-render srcDoc down.
//
// Default view is the ACTIVE slide only (so the preview shows exactly what the
// editor is editing); a "Whole deck" toggle keeps the full stacked view. Both
// come from the SAME server-built export document. V1 derives a per-page isolated
// srcDoc; workspace-shell V2 holds one marked document while the strict hydrate
// toggles pages in-frame. If the active page is absent, both paths show the honest
// whole-deck fallback, so the preview never goes blank once an export exists.
//
// On-demand refresh (BUILD CONTRACT): NO per-keystroke auto-refresh. "Refresh
// preview" rebuilds (save + export); a stale chip warns when deck STRUCTURE
// differs from what the shown preview was built from. The button is enabled as
// soon as the deck has a slide; if no subject is picked, clicking surfaces the
// clear "Pick a subject supplier first" error (a subject is required to
// save/export).
//
// Lane 299 (QA-8): PRESENTATION-ONLY re-skin to the Linear `.dk` class layer
// (DeckAssembler.linear.css). No behavior change. The iframe stays a light deck
// artifact on a neutral matte in either app theme.
//
// iframe sandbox is "allow-scripts" VERBATIM and must never gain allow-same-origin
// -- the export's own scaler, V1's static isolation script, and V2's editor
// visibility pass run there; a srcDoc frame WITHOUT same-origin is a
// null-origin frame with no access to this app's DOM/cookies/storage.
//
// ⭐ PLAN B PHASE 2 (Lane 1129, 2026-08-31): this pane is now ALSO the message
// boundary between the app and the preview frame's inline editor. It owns the
// iframe element, so it performs the first two steps of the plan-§4.2
// verification chain -- (1) event.source must be THIS iframe's contentWindow,
// (2) exact source/version/type + bounded primitive field types with NO extra
// fields on an edit -- and forwards typed callbacks up; DeckAssembler performs
// the authority steps (structure revision, exact instance, token editability,
// length). `event.origin` is deliberately not checked: a srcDoc sandbox frame
// has origin "null", so the contentWindow identity IS the check. Outbound, the
// pane sends the versioned `hydrate` message (plan §4.3) on frame load, on the
// frame's own `ready`, and whenever the active instance / overrides / mode /
// enabled / revision inputs change -- the frame applies textContent only.

import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

export function ModeToggle({ mode, onChange }) {
  const opt = (value, label) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={mode === value ? 'on' : ''}
      aria-pressed={mode === value}
    >
      {label}
    </button>
  );
  return (
    <div className="dk-seg">
      {/* Lane 789: "This page" / "Whole deck" -- the redesign says "page"
          everywhere the buyer is looking at one of the things in the rail. */}
      {opt('slide', 'This page')}
      {opt('deck', 'Whole deck')}
    </div>
  );
}

// The right panel show/hide, as an icon. It is a VIEW control, not a deck
// action, so it does not belong in the one toolbar with save + download.
function PanelIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      aria-hidden="true" focusable="false">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M15 4v16" />
    </svg>
  );
}

// Exact inbound schemas (plan §4.2): no missing keys, no extra keys. Keeping
// this check at the frame boundary means authority handlers never have to
// guess whether an absent primitive was deliberate or malformed.
const VIEWPORT_KEYS = ['source', 'version', 'structureRevision', 'type', 'instanceIndex'];
const READY_KEYS = ['source', 'version', 'structureRevision', 'type'];
const EDIT_KEYS = ['source', 'version', 'structureRevision', 'type',
  'instanceIndex', 'token', 'value'];
const UNDO_KEYS = ['source', 'version', 'structureRevision', 'type'];
const FIT_KEYS = ['source', 'version', 'structureRevision', 'type',
  'fitRevision', 'fits', 'instanceIndex'];
const ROW_ADD_KEYS = ['source', 'version', 'structureRevision', 'type',
  'blockKey', 'instanceIndex'];
const ROW_DELETE_KEYS = ['source', 'version', 'structureRevision', 'type',
  'blockKey', 'rowIndex', 'instanceIndex'];
const V2_SELECT_KEYS = ['source', 'version', 'structureRevision', 'type',
  'instanceIndex', 'elementId', 'digest', 'base', 'dualContext', 'nudgeable', 'rowBase'];
const V2_EDIT_KEYS = ['source', 'version', 'structureRevision', 'type',
  'fitRevision', 'instanceIndex', 'elementId', 'digest', 'value', 'rowBase'];
const V2_NUDGE_KEYS = ['source', 'version', 'structureRevision', 'type',
  'fitRevision', 'instanceIndex', 'elementId', 'digest', 'dx', 'dy', 'rowBase'];
const V2_ADDED_ADD_KEYS = ['source', 'version', 'structureRevision', 'type',
  'fitRevision', 'instanceIndex'];
const V2_ADDED_SELECT_KEYS = ['source', 'version', 'structureRevision', 'type',
  'instanceIndex', 'addedId'];
const V2_ADDED_DELETE_KEYS = ['source', 'version', 'structureRevision', 'type',
  'fitRevision', 'instanceIndex', 'addedId'];
const V2_ADDED_MOVE_KEYS = ['source', 'version', 'structureRevision', 'type',
  'fitRevision', 'instanceIndex', 'addedId', 'x', 'y'];
const V2_ADDED_RESIZE_KEYS = ['source', 'version', 'structureRevision', 'type',
  'fitRevision', 'instanceIndex', 'addedId', 'w'];
const V2_HISTORY_KEYS = ['source', 'version', 'structureRevision', 'type', 'instanceIndex'];
const V2_ROW_ADD_KEYS = ['source', 'version', 'structureRevision', 'type',
  'blockKey', 'blockOrdinal', 'instanceIndex', 'fitRevision', 'rowBase'];
const V2_ROW_DELETE_KEYS = ['source', 'version', 'structureRevision', 'type',
  'blockKey', 'blockOrdinal', 'rowId', 'instanceIndex', 'fitRevision', 'rowBase'];
const ELEMENT_ID_RE = /^(?:text\.e[1-9][0-9]{0,3}|e[1-9][0-9]{0,3}|(?:(?:b[1-9][0-9]{0,2}\.i[1-9][0-9]{0,2}|r[1-9][0-9]{0,2}\.[A-Za-z0-9_-]{1,32})\.)+e[1-9][0-9]{0,3})$/;
const DIGEST_RE = /^[0-9a-f]{64}$/;

function hasExactKeys(value, keys) {
  const actual = Object.keys(value);
  return actual.length === keys.length
    && keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}

function validRowBase(value) {
  if (value === null) return true;
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || !hasExactKeys(value, ['blockKey', 'blockOrdinal', 'rows'])
    || typeof value.blockKey !== 'string' || value.blockKey.length > 40
    || !Number.isInteger(value.blockOrdinal) || value.blockOrdinal < 1
    || !Array.isArray(value.rows) || value.rows.length > 50) return false;
  return value.rows.every((row) => row && typeof row === 'object' && !Array.isArray(row)
    && hasExactKeys(row, ['row_id', 'values'])
    && typeof row.row_id === 'string' && /^[A-Za-z0-9_-]{1,32}$/.test(row.row_id)
    && row.values && typeof row.values === 'object' && !Array.isArray(row.values)
    && Object.keys(row.values).length <= 20
    && Object.values(row.values).every((v) => typeof v === 'string' && v.length <= 2000));
}

function DeckPreviewPane({
  srcDoc, omitted = [], audienceNotice = '', evidenceNotices = [], stale, busy, canRefresh, onRefresh,
  mode, onModeChange, inspectorOpen, onToggleInspector, updatedLabel, needsSlides, needsSubject,
  frameRef = null,
  // Lane 1233 / W2: v2-only — the stage fills its zone's height (the
  // workspace is viewport-budgeted) instead of enforcing the frameHeight
  // floor; absent/false keeps the exact V1 sizing.
  fillHeight = false,
  // The desktop shell owns a single fixed slide viewport; whole-deck may scroll.
  fixedSlide = false,
  toolbarElsewhere = false,
  previewOnly = false, frameHeight = 620, editor = null,
}) {
  const ownedIframeRef = useRef(null);
  const iframeRef = frameRef || ownedIframeRef;
  const protocolRef = useRef(1);
  const protocolReadyRef = useRef(false);
  // Callbacks/values live in a ref so the message listener is registered ONCE
  // and never misses a frame message during a re-render window.
  const editorRef = useRef(editor);
  editorRef.current = editor;

  const sendHydrate = useCallback(() => {
    const ed = editorRef.current;
    const frame = iframeRef.current;
    if (!ed || !frame || !frame.contentWindow) return;
    // targetOrigin must be '*': the sandboxed srcDoc frame is origin "null"
    // and no concrete origin string can address it. The payload carries no
    // secrets -- it is the buyer's own overrides for their own preview.
    const version = ed.shell2 ? 2 : protocolRef.current;
    const payload = {
      source: 'deck-parent',
      version,
      type: 'hydrate',
      structureRevision: ed.revision || '',
      fitRevision: version === 2 && typeof ed.fitRevisionV2 === 'string'
        ? ed.fitRevisionV2
        : (typeof ed.fitRevision === 'string' ? ed.fitRevision : ''),
      activeInstance: Number.isInteger(ed.activeInstance) ? ed.activeInstance : -1,
      mode: ed.mode === 'slide' ? 'slide' : 'deck',
      enabled: ed.enabled === true,
      rows: ed.rows || null,
    };
    if (version === 2) {
      payload.patches = ed.patches || {};
      payload.addedElements = Array.isArray(ed.addedElements) ? ed.addedElements : [];
      payload.inlineEnabled = ed.inlineEnabled === true;
      payload.measure = Array.isArray(ed.measure) ? ed.measure : [];
      if (ed.shell2 && Array.isArray(ed.order)) payload.order = ed.order;
    }
    else {
      payload.overrides = ed.overrides || {};
      // Loaded-deck fit sweep (v1 protocol ONLY): the still-pending, NON-active
      // pages the frame should ALSO measure on this hydrate (each
      // {instanceIndex, fitRevision}). Empty for a deck with nothing pending.
      // Without this, a loaded deck's edited-but-unvisited pages stay
      // fitPending forever and grey the downloads. It rides the v1 branch
      // because the v2 element-editor hydrate schema (ELEMENT_EDITOR_JS) is a
      // strict exact-keys match with NO `measure` key -- carrying it on a v2
      // hydrate would fail that check and break element editing. The v1 editor
      // (EDITOR_JS) is the one that gained measureAndReport + the sweep loop.
      payload.measure = Array.isArray(ed.measure) ? ed.measure : [];
    }
    frame.contentWindow.postMessage(payload, '*');
  }, [iframeRef]);

  // Re-hydrate whenever any editor input changes (page switch, undo, panel
  // edit, mode flip, refreshed doc). The frame ignores no-op assignments.
  const edRevision = editor ? editor.revision : null;
  const edFitRevision = editor ? editor.fitRevision : null;
  const edFitRevisionV2 = editor ? editor.fitRevisionV2 : null;
  const edActive = editor ? editor.activeInstance : null;
  const edOverrides = editor ? editor.overrides : null;
  const edMode = editor ? editor.mode : null;
  const edEnabled = editor ? editor.enabled : null;
  const edInlineEnabled = editor ? editor.inlineEnabled : null;
  const edRows = editor ? editor.rows : null;
  const edPatches = editor ? editor.patches : null;
  const edAddedElements = editor ? editor.addedElements : null;
  // Loaded-deck fit sweep: re-hydrate when the parent's list of
  // extra NON-active pages to measure changes, so a freshly loaded deck's
  // pending pages get swept and the downloads can un-grey without click-through.
  const edMeasure = editor ? editor.measure : null;
  const shell2 = editor?.shell2 === true;
  const edOrder = editor?.order;
  const resetProtocol = useCallback(() => {
    protocolRef.current = editorRef.current?.shell2 ? 2 : 1;
    protocolReadyRef.current = false;
    const ed = editorRef.current;
    if (ed && ed.onProtocolVersion) ed.onProtocolVersion(0);
  }, []);
  // S7 listens before the iframe can announce ready. Legacy timing is unchanged.
  useLayoutEffect(() => { if (shell2) resetProtocol(); }, [shell2, srcDoc, resetProtocol]);
  useEffect(() => { if (!shell2) resetProtocol(); }, [shell2, srcDoc, resetProtocol]);

  useEffect(() => {
    sendHydrate();
  }, [sendHydrate, srcDoc, edRevision, edFitRevision, edFitRevisionV2, edActive, edOverrides, edMode,
    edEnabled, edInlineEnabled, edRows, edPatches, edAddedElements, edMeasure, edOrder]);

  const listenToFrame = useCallback(() => {
    const handle = (e) => {
      const frame = iframeRef.current;
      // §4.2 step 1: the ONLY accepted sender is this pane's own frame.
      if (!frame || e.source !== frame.contentWindow) return;
      const d = e.data;
      // §4.2 step 2: exact source/version + a known type, nothing else.
      if (!d || typeof d !== 'object' || d.source !== 'deck-editor'
        || ![1, 2].includes(d.version) || typeof d.type !== 'string') return;
      const ed = editorRef.current;
      if (!ed) return;
      if (d.type === 'ready') {
        if (!hasExactKeys(d, READY_KEYS) || typeof d.structureRevision !== 'string') return;
        const expected = ed.expectedVersion === 2 ? 2 : 1;
        if (d.version !== expected) return;
        if (protocolReadyRef.current && protocolRef.current !== d.version) return;
        protocolRef.current = d.version;
        protocolReadyRef.current = true;
        if (ed.onProtocolVersion) ed.onProtocolVersion(d.version);
        sendHydrate();
        return;
      }
      if (protocolRef.current !== d.version) return;
      if (d.version === 2 && d.type === 'viewport') {
        if (!ed.shell2 || !protocolReadyRef.current || !hasExactKeys(d, VIEWPORT_KEYS)
          || d.structureRevision !== ed.revision || !Number.isInteger(d.instanceIndex)
          || !Array.isArray(ed.order) || !ed.order.includes(d.instanceIndex)) return;
        ed.onViewport?.({ instanceIndex: d.instanceIndex, structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && d.type === 'select') {
        const removalKeys = ed.shell2 && Object.prototype.hasOwnProperty.call(d, 'removable');
        if (!hasExactKeys(d, removalKeys ? [...V2_SELECT_KEYS, 'removable', 'editing'] : V2_SELECT_KEYS)
          || (removalKeys && (typeof d.removable !== 'boolean' || typeof d.editing !== 'boolean'))
          || !Number.isInteger(d.instanceIndex) || d.instanceIndex < 0
          || typeof d.elementId !== 'string' || !ELEMENT_ID_RE.test(d.elementId)
          || typeof d.digest !== 'string' || !DIGEST_RE.test(d.digest)
          || typeof d.base !== 'string' || d.base.length > 2000
          || typeof d.dualContext !== 'boolean'
          || typeof d.nudgeable !== 'boolean'
          || !validRowBase(d.rowBase)) return;
        if (ed.onSelect) ed.onSelect({ instanceIndex: d.instanceIndex,
          elementId: d.elementId, digest: d.digest, base: d.base,
          dualContext: d.dualContext, nudgeable: d.nudgeable, rowBase: d.rowBase,
          ...(removalKeys ? { removable: d.removable, editing: d.editing } : {}),
          structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && d.type === 'edit') {
        const removing = ed.shell2 && d.remove === true;
        if (!hasExactKeys(d, removing ? V2_EDIT_KEYS.filter(k => k !== 'value').concat('remove') : V2_EDIT_KEYS)
          || !Number.isInteger(d.instanceIndex) || d.instanceIndex < 0
          || typeof d.elementId !== 'string' || !ELEMENT_ID_RE.test(d.elementId)
          || typeof d.digest !== 'string' || !DIGEST_RE.test(d.digest)
          || (!removing && (typeof d.value !== 'string' || d.value.length > 2000 || /[\r\n]/.test(d.value)))
          || typeof d.fitRevision !== 'string'
          || !validRowBase(d.rowBase)) return;
        if (ed.onElementEdit) ed.onElementEdit({ instanceIndex: d.instanceIndex,
          elementId: d.elementId, digest: d.digest, value: d.value,
          ...(removing ? { remove: true } : {}),
          fitRevision: d.fitRevision, rowBase: d.rowBase,
          structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && d.type === 'nudge') {
        if (!hasExactKeys(d, V2_NUDGE_KEYS)
          || !Number.isInteger(d.instanceIndex) || d.instanceIndex < 0
          || typeof d.elementId !== 'string' || !ELEMENT_ID_RE.test(d.elementId)
          || typeof d.digest !== 'string' || !DIGEST_RE.test(d.digest)
          || typeof d.dx !== 'number' || !Number.isFinite(d.dx)
          || typeof d.dy !== 'number' || !Number.isFinite(d.dy)
          || typeof d.fitRevision !== 'string' || !validRowBase(d.rowBase)) return;
        if (ed.onElementNudge) ed.onElementNudge({ instanceIndex: d.instanceIndex,
          elementId: d.elementId, digest: d.digest, dx: d.dx, dy: d.dy,
          fitRevision: d.fitRevision, rowBase: d.rowBase,
          structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && d.type === 'added-add') {
        if (!hasExactKeys(d, V2_ADDED_ADD_KEYS)
          || !Number.isInteger(d.instanceIndex) || d.instanceIndex < 0
          || typeof d.fitRevision !== 'string') return;
        if (ed.onAddedAdd) ed.onAddedAdd({ instanceIndex: d.instanceIndex,
          fitRevision: d.fitRevision, structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && d.type === 'added-select') {
        if (!hasExactKeys(d, V2_ADDED_SELECT_KEYS)
          || !Number.isInteger(d.instanceIndex) || d.instanceIndex < 0
          || typeof d.addedId !== 'string' || !/^a[1-9][0-9]{0,2}$/.test(d.addedId)) return;
        if (ed.onAddedSelect) ed.onAddedSelect({ instanceIndex: d.instanceIndex,
          addedId: d.addedId, structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && d.type === 'added-delete') {
        if (!ed.shell2 || !protocolReadyRef.current || !ed.enabled
          || !hasExactKeys(d, V2_ADDED_DELETE_KEYS) || d.structureRevision !== ed.revision
          || !Number.isInteger(d.instanceIndex) || d.instanceIndex < 0
          || !ed.order?.includes(d.instanceIndex) || typeof d.fitRevision !== 'string'
          || typeof d.addedId !== 'string' || !/^a[1-9][0-9]{0,2}$/.test(d.addedId)) return;
        ed.onAddedDelete?.({ instanceIndex: d.instanceIndex, addedId: d.addedId,
          fitRevision: d.fitRevision, structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && (d.type === 'added-move' || d.type === 'added-resize')) {
        const keys = d.type === 'added-move' ? V2_ADDED_MOVE_KEYS : V2_ADDED_RESIZE_KEYS;
        if (!hasExactKeys(d, keys) || !Number.isInteger(d.instanceIndex)
          || d.instanceIndex < 0 || typeof d.addedId !== 'string'
          || !/^a[1-9][0-9]{0,2}$/.test(d.addedId)
          || typeof d.fitRevision !== 'string'
          || (d.type === 'added-move' && (typeof d.x !== 'number'
            || !Number.isFinite(d.x) || typeof d.y !== 'number' || !Number.isFinite(d.y)))
          || (d.type === 'added-resize' && (typeof d.w !== 'number'
            || !Number.isFinite(d.w)))) return;
        const callback = d.type === 'added-move' ? ed.onAddedMove : ed.onAddedResize;
        if (callback) callback({ instanceIndex: d.instanceIndex, addedId: d.addedId,
          x: d.x, y: d.y, w: d.w, fitRevision: d.fitRevision,
          structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && (d.type === 'undo-request' || d.type === 'redo-request')) {
        if (!hasExactKeys(d, V2_HISTORY_KEYS)
          || !Number.isInteger(d.instanceIndex) || d.instanceIndex < 0
          || typeof d.structureRevision !== 'string') return;
        const callback = d.type === 'undo-request' ? ed.onElementUndo : ed.onElementRedo;
        if (callback) callback({ instanceIndex: d.instanceIndex,
          structureRevision: d.structureRevision });
        return;
      }
      if (d.version === 2 && (d.type === 'row-add' || d.type === 'row-delete')) {
        const keys = d.type === 'row-add' ? V2_ROW_ADD_KEYS : V2_ROW_DELETE_KEYS;
        if (!hasExactKeys(d, keys) || typeof d.blockKey !== 'string'
          || d.blockKey.length > 40 || !Number.isInteger(d.blockOrdinal)
          || d.blockOrdinal < 1 || !Number.isInteger(d.instanceIndex)
          || d.instanceIndex < 0 || typeof d.fitRevision !== 'string'
          || !validRowBase(d.rowBase)
          || (d.type === 'row-delete'
            && (typeof d.rowId !== 'string' || !/^[A-Za-z0-9_-]{1,32}$/.test(d.rowId)))) return;
        const callback = d.type === 'row-add' ? ed.onElementRowAdd : ed.onElementRowDelete;
        if (callback) callback({ blockKey: d.blockKey, blockOrdinal: d.blockOrdinal,
          rowId: d.rowId, instanceIndex: d.instanceIndex,
          fitRevision: d.fitRevision, rowBase: d.rowBase,
          structureRevision: d.structureRevision });
        return;
      }
      if (d.type === 'edit') {
        if (!hasExactKeys(d, EDIT_KEYS)) return;
        if (!Number.isInteger(d.instanceIndex) || d.instanceIndex < 0) return;
        if (typeof d.token !== 'string' || d.token.length === 0
          || d.token.length > 100) return;
        if (typeof d.value !== 'string' || d.value.length > 2000) return;
        if (typeof d.structureRevision !== 'string') return;
        if (ed.onEdit) {
          ed.onEdit({
            instanceIndex: d.instanceIndex,
            token: d.token,
            value: d.value,
            structureRevision: d.structureRevision,
          });
        }
        return;
      }
      if (d.type === 'undo-request') {
        if (!hasExactKeys(d, UNDO_KEYS) || typeof d.structureRevision !== 'string') return;
        if (ed.onUndoRequest) {
          ed.onUndoRequest({ structureRevision: d.structureRevision });
        }
        return;
      }
      if (d.type === 'fit-status') {
        if (!hasExactKeys(d, FIT_KEYS)) return;
        if (typeof d.structureRevision !== 'string'
          || typeof d.fitRevision !== 'string' || typeof d.fits !== 'boolean') return;
        if (!Number.isInteger(d.instanceIndex) || d.instanceIndex < 0) return;
        if (ed.onFitStatus) {
          ed.onFitStatus({ fits: d.fits,
            instanceIndex: d.instanceIndex,
            structureRevision: d.structureRevision,
            fitRevision: d.fitRevision });
        }
        return;
      }
      // Plan B Phase 3: structural row operations — versioned, strict-schema,
      // authority stays with DeckAssembler (contract + bounds + revision).
      if (d.type === 'row-add') {
        if (!hasExactKeys(d, ROW_ADD_KEYS)) return;
        if (typeof d.blockKey !== 'string' || d.blockKey.length > 40) return;
        if (typeof d.structureRevision !== 'string') return;
        if (!Number.isInteger(d.instanceIndex) || d.instanceIndex < 0) return;
        if (ed.onRowAdd) {
          ed.onRowAdd({ blockKey: d.blockKey,
            instanceIndex: d.instanceIndex,
            structureRevision: d.structureRevision });
        }
        return;
      }
      if (d.type === 'row-delete') {
        if (!hasExactKeys(d, ROW_DELETE_KEYS)) return;
        if (typeof d.blockKey !== 'string' || d.blockKey.length > 40) return;
        if (!Number.isInteger(d.rowIndex) || d.rowIndex < 1
          || d.rowIndex > 99) return;
        if (typeof d.structureRevision !== 'string') return;
        if (!Number.isInteger(d.instanceIndex) || d.instanceIndex < 0) return;
        if (ed.onRowDelete) {
          ed.onRowDelete({ blockKey: d.blockKey, rowIndex: d.rowIndex,
            instanceIndex: d.instanceIndex,
            structureRevision: d.structureRevision });
        }
      }
      // every other type: ignored (reject-by-ignore, plan §4.2)
    };
    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, [sendHydrate, iframeRef]);

  useLayoutEffect(() => (shell2 ? listenToFrame() : undefined), [shell2, listenToFrame]);
  useEffect(() => (!shell2 ? listenToFrame() : undefined), [shell2, listenToFrame]);

  const handleFrameLoad = useCallback(() => {
    // Preserve the legacy eager hydrate. A v2 frame rejects it and immediately
    // answers with ready/version 2, at which point the latched v2 hydrate runs.
    sendHydrate();
  }, [sendHydrate]);

  // Context-aware empty-state hint so the user knows exactly what is missing
  // (the old "Add at least one slide and pick a subject" misled when slides
  // already existed).
  const emptyHint = toolbarElsewhere
    ? (needsSlides ? 'Add slides from the library.' : needsSubject ? 'Pick a subject supplier first.' : 'Your deck will appear here.')
    : needsSlides
    ? 'Add slides from the library, then refresh the preview.'
    : needsSubject
      ? 'Pick a subject supplier first, then refresh the preview.'
      : 'Click "Refresh preview" to build the preview, then pick a slide to view it here.';

  // Lane 789: previewOnly (the generate-a-page modal) keeps the OLD bare shape --
  // it is a preview inside a modal, not the builder's hero pane, and giving it a
  // matte stage would re-skin a surface this lane was not asked to touch. It
  // carries no editor and no ref -- the message boundary belongs to the hero
  // pane only.
  if (previewOnly) {
    return srcDoc ? (
      <iframe
        title="Deck preview"
        sandbox="allow-scripts"
        srcDoc={srcDoc}
        className="dk-frame"
        style={{ height: `${frameHeight}px` }}
      />
    ) : (
      <div className="dk-pv-empty">{emptyHint}</div>
    );
  }

  return (
    <>
      {/* Lane 789: the toolbar is now the PANEL HEADER, and the page below it
          sits on a matte -- the preview is the hero of this page, so it is the
          biggest thing on it. Every control that was here is still here. */}
      {!toolbarElsewhere && <div className="dk-pvh">
        <ModeToggle mode={mode} onChange={onModeChange} />
        <div className="dk-pv-right">
          {/* When the deck changed since the last build, warn; otherwise show
              when the shown preview was last built. */}
          {stale ? (
            <span className="dk-pv-stale">Out of date</span>
          ) : (updatedLabel ? (
            <span className="dk-pv-upd">Built {updatedLabel}</span>
          ) : null)}
          <button
            type="button"
            className={`dk-btn sm${stale || !srcDoc ? ' pri' : ''}`}
            disabled={busy || !canRefresh}
            onClick={onRefresh}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {busy ? 'Working…' : (srcDoc ? 'Refresh preview' : 'Build the preview')}
          </button>
          {/* Phase 2: show/hide the right panel. The open state + grid reflow
              live in the DeckAssembler parent; this button only flips it.
              Plan B §7.5: it is the PAGE panel -- words are one section of it. */}
          {onToggleInspector && (
            <button
              type="button"
              className={`dk-btn sm ghost${inspectorOpen ? ' on' : ''}`}
              onClick={onToggleInspector}
              aria-pressed={!!inspectorOpen}
              aria-label="Page panel"
              title={inspectorOpen ? 'Hide the page panel' : 'Show the page panel'}
              style={{ lineHeight: 0, padding: '5px 7px' }}
            >
              <PanelIcon />
            </button>
          )}
        </div>
      </div>}

      {omitted.length > 0 && (
        <div className="dk-pv-omit" style={{ margin: '10px 12px 0' }}>
          {omitted.length} slide(s) were unavailable and omitted: {omitted.join(', ')}.
        </div>
      )}

      {/* Lane 905. A page the buyer added, gone from the deck with no
          explanation, reads as a broken product -- and the worse case is that
          they never notice. This is the whole sentence, composed server-side so
          the copy has ONE home, shown beside the preview where the page went
          missing. It is deliberately NOT rendered into the exported file: that
          file goes to the supplier, and naming what we withheld would tell them
          we hold it. */}
      {Array.isArray(evidenceNotices) && evidenceNotices.filter(note => typeof note === 'string').map((note, index) => (
        <div key={index} className="dk-pv-omit" style={{ margin: '10px 12px 0' }} role="status">{note}</div>
      ))}
      {audienceNotice ? (
        <div className="dk-pv-omit" style={{ margin: '10px 12px 0' }}>
          {audienceNotice}
        </div>
      ) : null}

      {/* The page FILLS the stage rather than sitting at a fixed height inside
          it -- `frameHeight` is the floor, not the ceiling, so the preview grows
          into whatever room the workspace has instead of leaving a band of matte
          under it. `previewOnly` (the generate modal) still uses the exact
          height its caller asks for. */}
      <div
        className={`dk-stage${fillHeight ? ' fit' : ''}${fillHeight && mode === 'deck' ? ' whole' : ''}`}
        style={fillHeight ? undefined : { minHeight: `${frameHeight}px` }}>
        {srcDoc ? (
          <iframe
            ref={iframeRef}
            onLoad={handleFrameLoad}
            scrolling={fixedSlide && mode === 'slide' ? 'no' : undefined}
            title="Deck preview"
            sandbox="allow-scripts"
            srcDoc={srcDoc}
            className="dk-frame fill"
          />
        ) : (
          <div className="dk-pv-empty">{emptyHint}</div>
        )}
      </div>
    </>
  );
}

export default DeckPreviewPane;
